import React, { useState, useMemo } from 'react';
import { useInbox } from '../InboxContext';
import { ThreadCard, groupEmailsIntoThreads } from '../shared/ThreadCard';

const COLUMNS = [
  { key: 'today',     label: 'Today',       accent: 'var(--red-critical)' },
  { key: 'scheduled', label: 'Scheduled',   accent: 'var(--yellow)' },
  { key: 'parking',   label: 'Parking Lot', accent: 'var(--text-muted)' },
];

// ── Mini calendar for schedule-on-drop ───────────────────────────────────────
const MC_DAYS = ['S','M','T','W','T','F','S'];
function MiniCal({ value, onChange }) {
  const todayObj = new Date();
  const init = () => {
    if (value) { const [y, m] = value.split('-').map(Number); return new Date(y, m - 1, 1); }
    return new Date(todayObj.getFullYear(), todayObj.getMonth(), 1);
  };
  const [view, setView] = useState(init);
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const dim = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  const toStr = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <button type="button" onClick={() => setView(new Date(year, month - 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>{'\u2039'}</button>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-deep)' }}>{view.toLocaleString('en-US', { month: 'long' })} {year}</span>
        <button type="button" onClick={() => setView(new Date(year, month + 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>{'\u203A'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
        {MC_DAYS.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', padding: '2px 0' }}>{d}</div>)}
        {cells.map((d, i) => d == null
          ? <div key={`e${i}`} />
          : <button key={d} type="button" onClick={() => onChange(toStr(d))} style={{
              textAlign: 'center', fontSize: 11, padding: '4px 0', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: value === toStr(d) ? 'var(--aqua)' : 'transparent',
              color: value === toStr(d) ? 'white' : 'var(--navy-deep)', fontWeight: value === toStr(d) ? 700 : 400,
            }}>{d}</button>
        )}
      </div>
    </div>
  );
}

function KanbanView() {
  const { emails, actions } = useInbox();
  const [dragThreadId, setDragThreadId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [schedulePopover, setSchedulePopover] = useState(null);
  const [schedDate, setSchedDate] = useState('');

  const queuedEmails = useMemo(
    () => emails.filter((e) => e.queueStatus === 'queued' || e.queueStatus === 'scheduled'),
    [emails],
  );

  // Group into threads
  const threads = useMemo(() => groupEmailsIntoThreads(queuedEmails), [queuedEmails]);

  // Assign each thread to a column based on the latest email's swimlane
  const threadsByCol = useMemo(() => {
    const map = { today: [], scheduled: [], parking: [] };
    threads.forEach((t) => {
      const lane = t.latestEmail.boardSwimlane || 'today';
      if (map[lane]) map[lane].push(t);
    });
    return map;
  }, [threads]);

  const handleDragStart = (e, threadId) => {
    setDragThreadId(threadId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colKey);
  };

  const handleDragLeave = () => setDragOverCol(null);

  const handleDrop = (e, colKey) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!dragThreadId) return;

    const thread = threads.find((t) => t.threadId === dragThreadId);
    if (!thread) { setDragThreadId(null); return; }

    const ids = thread.emails.map((em) => em.id);

    if (colKey === 'scheduled') {
      setSchedDate('');
      setSchedulePopover({ ids, x: e.clientX, y: e.clientY });
    } else {
      ids.forEach((id) => actions.boardEmail(id, colKey));
    }
    setDragThreadId(null);
  };

  const confirmScheduleDrop = () => {
    if (!schedDate || !schedulePopover) return;
    schedulePopover.ids.forEach((id) => actions.scheduleEmail(id, schedDate));
    setSchedulePopover(null);
  };

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, padding: 8 }}>
      {COLUMNS.map(({ key, label, accent }) => {
        const items = threadsByCol[key] || [];
        const isOver = dragOverCol === key;
        return (
          <div
            key={key}
            onDragOver={(e) => handleDragOver(e, key)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, key)}
            style={{
              display: 'flex', flexDirection: 'column', borderRadius: 8, overflow: 'hidden',
              background: isOver ? 'var(--aqua-glow)' : 'var(--surface-warm)',
              transition: 'background 0.15s',
              outline: isOver ? '2px dashed var(--aqua)' : 'none',
            }}
          >
            {/* Column header */}
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderBottom: `2px solid ${accent}` }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-deep)' }}>{label}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: accent }}>{items.length}</span>
            </div>

            {/* Thread cards */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {items.map((thread) => (
                <ThreadCard
                  key={thread.threadId}
                  thread={thread}
                  variant="kanban"
                  draggable
                  onDragStart={(e) => handleDragStart(e, thread.threadId)}
                  style={{
                    cursor: 'grab',
                    opacity: dragThreadId === thread.threadId ? 0.5 : 1,
                    transition: 'opacity 0.15s',
                  }}
                />
              ))}
              {items.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 8px', fontStyle: 'italic' }}>
                  {isOver ? 'Drop here' : 'Empty'}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Schedule popover */}
      {schedulePopover && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setSchedulePopover(null)} />
          <div style={{
            position: 'fixed',
            top: Math.min(schedulePopover.y, window.innerHeight - 320),
            left: Math.min(schedulePopover.x, window.innerWidth - 280),
            zIndex: 1050,
            background: 'var(--white)', borderRadius: 9, padding: 12,
            boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)', minWidth: 240,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-deep)', marginBottom: 8 }}>Select schedule date</div>
            <MiniCal value={schedDate} onChange={setSchedDate} />
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button type="button" onClick={confirmScheduleDrop}
                style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
                  background: schedDate ? 'var(--aqua)' : 'var(--surface)',
                  color: schedDate ? 'white' : 'var(--text-muted)', cursor: schedDate ? 'pointer' : 'default' }}>Confirm</button>
              <button type="button" onClick={() => setSchedulePopover(null)}
                style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5,
                  border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { KanbanView };
